import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Column } from '@components/sharedComponents/dataTable';
import { DrawRequest } from '@models/drawRequests/drawRequest';
import {
  Delete as DeleteIcon,
  Block as BanIcon,
  InfoOutlined as InfoIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import FormattedDate from '@helpers/formattedDate';
import FormattedCurrency from '@helpers/formattedCurrency';
import { useDrawRequestCommandApi } from '@hooks/api/useDrawRequestCommandApi';
import {
  DRAW_REQUEST_STATUS,
  DRAW_REQUEST_STATUS_LABELS,
} from '@constants/drawManagerConstants';

export const getStatusChipColor = (status: number, theme: any) => {
  switch (status) {
    case DRAW_REQUEST_STATUS.NEW:
      return {
        label: DRAW_REQUEST_STATUS_LABELS[DRAW_REQUEST_STATUS.NEW],
        bgColor: theme.palette.blue[100],
        borderRadius: '24px',
        textColor: theme.palette.blue[500],
      };
    case DRAW_REQUEST_STATUS.IN_REVIEW:
      return {
        label: DRAW_REQUEST_STATUS_LABELS[DRAW_REQUEST_STATUS.IN_REVIEW],
        bgColor: theme.palette.grey[200],
        borderRadius: '24px',
        textColor: theme.palette.text.primary,
      };
    case DRAW_REQUEST_STATUS.APPROVED:
      return {
        label: DRAW_REQUEST_STATUS_LABELS[DRAW_REQUEST_STATUS.APPROVED],
        bgColor: theme.palette.green[100],
        borderRadius: '24px',
        textColor: theme.palette.green[500],
      };
    case DRAW_REQUEST_STATUS.DISBURSED:
      return {
        label: DRAW_REQUEST_STATUS_LABELS[DRAW_REQUEST_STATUS.DISBURSED],
        bgColor: theme.palette.purple[100],
        borderRadius: '24px',
        textColor: theme.palette.purple[500],
      };
    case DRAW_REQUEST_STATUS.CANCELLED:
      return {
        label: DRAW_REQUEST_STATUS_LABELS[DRAW_REQUEST_STATUS.CANCELLED],
        bgColor: theme.palette.red[100],
        borderRadius: '24px',
        textColor: theme.palette.red[500],
      };
    default:
      return {
        label: status.toString(),
        bgColor: 'default',
        borderRadius: '24px',
        textColor: theme.palette.blue[500],
      };
  }
};

export default function BudgetItemsCell({
  drawRequest,
}: {
  drawRequest: DrawRequest;
}) {
  const budgetItemNames = drawRequest.budgetItemNames || [];

  const renderNoItems = () => (
    <Box
      sx={{
        fontFamily: 'Epilogue',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '150%',
        color: 'text.secondary',
        textAlign: 'left',
        width: '100%',
      }}
    >
      No items
    </Box>
  );

  const renderSingleItem = () => (
    <Box
      sx={{
        fontFamily: 'Epilogue',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '150%',
        color: 'text.primary',
        textAlign: 'left',
        width: '100%',
      }}
    >
      {budgetItemNames[0]}
    </Box>
  );

  const renderTooltipContent = () => (
    <Box>
      {budgetItemNames.map((budgetItemDescription, index) => (
        <Typography
          key={index}
          variant="body3"
          sx={{
            mb: 0.5,
            color: 'white',
            display: 'block',
            lineHeight: 1.4,
          }}
        >
          • {budgetItemDescription}
        </Typography>
      ))}
    </Box>
  );

  const renderInfoButton = () => (
    <Tooltip
      title={renderTooltipContent()}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: 'primary.main',
            fontSize: '13px',
            maxWidth: '300px',
            padding: '8px 12px',
          },
        },
      }}
    >
      <IconButton
        size="small"
        sx={{
          width: 20,
          height: 20,
          padding: 0,
          color: 'text.secondary',
        }}
      >
        <InfoIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );

  const renderMultipleItems = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1,
        width: '100%',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          textAlign: 'left',
        }}
      >
        Multiple items
      </Typography>
      {renderInfoButton()}
    </Box>
  );

  const renderContent = () => {
    if (budgetItemNames.length === 0) {
      return renderNoItems();
    }

    if (budgetItemNames.length === 1) {
      return renderSingleItem();
    }

    return renderMultipleItems();
  };

  return renderContent();
}

export const createDrawRequestColumns = (
  handleViewDrawRequest: (row: DrawRequest) => void,
  refetch?: () => void,
  sourceRecId?: string
): Column<DrawRequest>[] => {
  const theme = useTheme();
  const deleteDrawRequestMutation =
    useDrawRequestCommandApi.useDeleteDrawRequest();
  const cancelDrawRequestMutation =
    useDrawRequestCommandApi.useCancelDrawRequest();

  const handleDeleteWithMutation = (drawRequestId: string) => {
    if (sourceRecId) {
      deleteDrawRequestMutation.mutate(
        {
          drawRequestId,
          sourceRecId,
        },
        {
          onSuccess: () => {
            if (refetch) {
              refetch();
            }
          },
          onError: error => {
            console.error('Failed to delete draw request:', error);
          },
        }
      );
    }
  };

  const handleCancelWithMutation = (drawRequestId: string) => {
    if (sourceRecId) {
      cancelDrawRequestMutation.mutate(
        {
          drawRequestId,
          sourceRecId,
        },
        {
          onSuccess: () => {
            if (refetch) {
              refetch();
            }
          },
          onError: error => {
            console.error('Failed to cancel draw request:', error);
          },
        }
      );
    }
  };

  const renderDeleteButton = (row: DrawRequest) => (
    <Tooltip title="Delete Draw Request" arrow>
      <IconButton
        size="small"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          handleDeleteWithMutation(row.id || '');
        }}
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          border: '1px solid',
          borderColor: 'error.main',
          color: 'error.main',
          '&:hover': {
            backgroundColor: 'error.main',
            color: 'white',
          },
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const renderCancelButton = (row: DrawRequest) => (
    <Tooltip title="Cancel Draw Request" arrow>
      <IconButton
        size="small"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          handleCancelWithMutation(row.id || '');
        }}
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          border: '1px solid',
          borderColor: 'primary.main',
          color: 'primary.main',
          '&:hover': {
            backgroundColor: 'primary.main',
            color: 'white',
          },
        }}
      >
        <BanIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const renderActions = (row: DrawRequest) => {
    const showDelete = row.status === DRAW_REQUEST_STATUS.NEW;
    const showCancel =
      row.status !== DRAW_REQUEST_STATUS.DISBURSED &&
      row.status !== DRAW_REQUEST_STATUS.CANCELLED;

    return (
      <Box
        className="action-buttons"
        sx={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'flex-end',
          opacity: 1,
          transition: 'opacity 0.2s ease-in-out',
        }}
      >
        {showDelete && renderDeleteButton(row)}
        {showCancel && renderCancelButton(row)}
      </Box>
    );
  };

  const renderRequestIdCell = (value: string, row?: DrawRequest) => (
    <Box
      component="button"
      onClick={e => {
        e.stopPropagation();
        if (row) {
          handleViewDrawRequest(row);
        }
      }}
      sx={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: 'primary.main',
        fontFamily: 'Epilogue',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '150%',
        textAlign: 'left',
        width: '100%',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
    >
      {value}
    </Box>
  );

  const renderDateCell = (value: Date) => (
    <Box
      sx={{
        fontFamily: 'Epilogue',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '150%',
        color: 'text.primary',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <FormattedDate
        value={value}
        showTime={false}
        options={{
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }}
      />
    </Box>
  );

  const renderCurrencyCell = (value: number) => (
    <Box
      sx={{
        fontFamily: 'Epilogue',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '150%',
        color: 'text.primary',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <FormattedCurrency value={value} />
    </Box>
  );

  const renderBudgetItemsCell = (_value: any, row?: DrawRequest) => {
    if (!row) return <></>;
    return <BudgetItemsCell drawRequest={row} />;
  };

  const renderStatusCell = (_value: number, row?: DrawRequest) => {
    const { label, bgColor, textColor, borderRadius } = getStatusChipColor(
      _value,
      theme
    );
    const hasUnreadMessages = row?.hasUnreadMessages ?? false;

    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          height: '100%',
          alignItems: 'center',
        }}
      >
        <Chip
          label={label}
          sx={{
            backgroundColor: bgColor,
            color: textColor,
            borderRadius: borderRadius,
            fontSize: '12px',
            height: '24px',
          }}
          size="small"
        />
        {hasUnreadMessages && (
          <Box
            sx={{
              position: 'absolute',
              top: '2px',
              right: '-8px',
            }}
          >
            <EmailIcon
              sx={{ fontSize: 14, color: 'primary.main' }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderActionsCell = (_value: any, row?: DrawRequest) => {
    if (!row) return <></>;
    return renderActions(row);
  };

  const createRequestIdColumn = () => ({
    id: 'drawRequestId' as keyof DrawRequest,
    label: 'Request ID',
    width: '15%',
    align: 'left' as const,
    isSticky: true,
    format: renderRequestIdCell,
  });

  const createDateColumn = () => ({
    id: 'dateCreated' as keyof DrawRequest,
    label: 'Date Sent',
    width: '12%',
    align: 'left' as const,
    isSticky: true,
    format: renderDateCell,
  });

  const createRequestAmountColumn = () => ({
    id: 'requestAmount' as keyof DrawRequest,
    label: 'Request',
    width: '12%',
    align: 'left' as const,
    isSticky: true,
    format: renderCurrencyCell,
  });

  const createBudgetAmountColumn = () => ({
    id: 'budgetAmount' as keyof DrawRequest,
    label: 'Budget',
    width: '15%',
    align: 'left' as const,
    isSticky: true,
    format: renderCurrencyCell,
  });

  const createBudgetItemsColumn = () => ({
    id: 'budgetItems' as keyof DrawRequest,
    label: 'Budget item(s)',
    width: '20%',
    align: 'left' as const,
    isSticky: true,
    sortable: false,
    format: renderBudgetItemsCell,
  });

  const createStatusColumn = () => ({
    id: 'status' as keyof DrawRequest,
    label: 'Status',
    width: '12%',
    align: 'left' as const,
    isSticky: true,
    format: renderStatusCell,
  });

  const createActionsColumn = () => ({
    id: 'actions' as keyof DrawRequest,
    label: '',
    align: 'right' as const,
    width: '8%',
    isSticky: true,
    format: renderActionsCell,
  });

  const createColumns = () => [
    createRequestIdColumn(),
    createDateColumn(),
    createRequestAmountColumn(),
    createBudgetAmountColumn(),
    createBudgetItemsColumn(),
    createStatusColumn(),
    createActionsColumn(),
  ];

  return createColumns();
};
